import { useState, useCallback } from 'react';
import { useHouseContext } from '../context/HouseContext';
import { Documents, DocumentWithDownloadUrl as Document } from '@/client';

/**
 * Hook for batch fetching presigned download URLs for multiple documents
 * First checks existing documents for URLs, then makes a single batch request for missing ones
 */
const usePresignedDownloadUrls = () => {
	const { selectedHouse } = useHouseContext();
	const [downloadUrls, setDownloadUrls] = useState<{ [key: number]: string }>({});
	const [isFetching, setIsFetching] = useState(false);

	// const fetchPresignedDownloadUrl = useCallback(async (documentId: number): Promise<string | null> => {
	//   if (!selectedHouse?.id) {
	//     return null;
	//   }
	//
	//   // Check if we already have the URL cached
	//   if (downloadUrls[documentId]) {
	//     return downloadUrls[documentId];
	//   }
	//
	//   try {
	//     // Use the SDK directly for single document (fallback)
	//     const result = await Documents.downloadDocumentOptimizedHousesHouseIdDocumentsDocumentIdDownloadGet({
	//       path: {
	//         house_id: selectedHouse.id,
	//         document_id: documentId
	//       }
	//     });
	//     const downloadData = result.data as DownloadResponse;
	//     const url = downloadData.download_url;
	//
	//     if (url) {
	//       setDownloadUrls(prev => ({ ...prev, [documentId]: url }));
	//       return url;
	//     }
	//     return null;
	//   } catch (error) {
	//     console.error('Failed to fetch presigned download URL:', error);
	//     return null;
	//   }
	// }, [selectedHouse?.id, downloadUrls]);

	const fetchBatchDownloadUrls = useCallback(
		async (documentIds: number[], documents: Document[] = []): Promise<{ [key: number]: string }> => {
			if (!selectedHouse?.id || documentIds.length === 0) {
				return {};
			}

			if (isFetching) {
				return {};
			}

			setIsFetching(true);

			try {
				// First, check existing documents for download URLs
				const urlsFromDocuments: { [key: number]: string } = {};
				const remainingDocumentIds: number[] = [];

				for (const docId of documentIds) {
					// Check if already cached
					if (downloadUrls[docId]) {
						urlsFromDocuments[docId] = downloadUrls[docId];
						continue;
					}

					// Check if document has download_url already
					const document = documents.find((doc) => doc.id === docId);
					if (document?.download_url) {
						urlsFromDocuments[docId] = document.download_url;
					} else {
						remainingDocumentIds.push(docId);
					}
				}

				// Update state with URLs found from documents
				if (Object.keys(urlsFromDocuments).length > 0) {
					setDownloadUrls((prev) => ({ ...prev, ...urlsFromDocuments }));
				}

				// If no remaining documents need fetching, return what we have
				if (remainingDocumentIds.length === 0) {
					return { ...downloadUrls, ...urlsFromDocuments };
				}

				// Make single batch request for remaining documents
				const result = await Documents.getBatchDownloadUrlsHousesHouseIdDocumentsBatchDownloadUrlsPost({
					path: {
						house_id: selectedHouse.id,
					},
					body: {
						document_ids: remainingDocumentIds,
					},
				});

				const batchResponse = result.data;
				const batchUrls: { [key: number]: string } = {};

				// Convert string keys back to numbers and extract URLs
				if (batchResponse?.download_urls) {
					Object.entries(batchResponse.download_urls).forEach(([key, value]) => {
						const docId = parseInt(key, 10);
						if (!isNaN(docId) && typeof value === 'string') {
							batchUrls[docId] = value;
						}
					});
				}

				// Update state with batch results
				const allUrls = { ...urlsFromDocuments, ...batchUrls };
				setDownloadUrls((prev) => ({ ...prev, ...allUrls }));

				return { ...downloadUrls, ...allUrls };
			} catch (error) {
				console.error('Failed to fetch batch download URLs:', error);
				return downloadUrls;
			} finally {
				setIsFetching(false);
			}
		},
		[selectedHouse?.id, downloadUrls, isFetching],
	);

	return {
		// fetchPresignedDownloadUrl,
		fetchBatchDownloadUrls,
		downloadUrls,
		isFetching,
		clearUrls: () => setDownloadUrls({}),
	};
};

export default usePresignedDownloadUrls;
